"use client"

import {
    AlertTriangle,
    Loader2,
    CheckCircle,
    XCircle,
  } from "lucide-react"
import type React from "react"
import { useState } from "react"
import { Link, useNavigate } from 'react-router-dom';

import { Button } from "../../components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import { deleteUserAccount, type DeleteAccountRequest } from "../../services/userService";


export default function DeleteAccountPage() {
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState("")
    const [success, setSuccess] = useState(false)
    const [deleteMessage, setDeleteMessage] = useState("")
    const [isOwnerFlow, setIsOwnerFlow] = useState(false)
    const navigate = useNavigate()
    const [formData, setFormData] = useState({
        email: "",
        password: "",
      })

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }))
        if (error) setError("")

        if (success) setSuccess(false)
      }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.email || !formData.password) {
          setError("Please fill in all required fields")
          return
        }

        setIsLoading(true)
        setError("")
        setSuccess(false)

        try {
          const request: DeleteAccountRequest = {
            email: formData.email.trim(),
            password: formData.password,
          }

          const response = await deleteUserAccount(request)

          if (response.success) {
            setSuccess(true)
            setDeleteMessage(response.message)
            setIsOwnerFlow(response.isOwner || false)

            // Clear form
            setFormData({ email: "", password: "" })

            // For owners, redirect after longer delay to allow reading the message
            // For regular users, redirect after 3 seconds
            const redirectDelay = response.isOwner ? 8000 : 3000
            setTimeout(() => {
              navigate("/")
            }, redirectDelay)
          } else {
            setError(response.message || "Failed to delete account")
          }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
          setError(err.message || "An unexpected error occurred")
        } finally {
          setIsLoading(false)
        }
    }

    // Show success state
    if (success) {
      return (
        <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter']">
          <main className="py-20 bg-[#111010] min-h-screen flex items-center justify-center">
            <div className="container mx-auto px-4 max-w-2xl">
              <Card className="bg-[#363232] border-[#504A4A]">
                <CardHeader className="text-center">
                  <div className="flex justify-center mb-4">
                    <CheckCircle className="w-12 h-12 text-green-500" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-[#EBFFEE]">
                    {isOwnerFlow ? "Support Request Sent" : "Account Deleted"}
                  </CardTitle>
                  <p className="text-[#A9A3A3] mt-2">
                    {deleteMessage}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <p className="text-[#A9A3A3] mb-4">
                      {isOwnerFlow 
                        ? "You will be redirected to the home page shortly. Our team will contact you within 1-2 business days."
                        : "You will be redirected to the home page shortly."
                      }
                    </p>
                    <Link to="/">
                      <Button className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]">
                        Go Home Now
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </main>
        </div>
      )
    }

    return (
        <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter']">
            {/* Main Content */}
            <main className="py-20 bg-[#111010] min-h-screen flex items-center justify-center">
            <div className="container mx-auto px-4 max-w-2xl">
                <Card className="bg-[#363232] border-[#504A4A]">
                <CardHeader className="text-center">
                    <div className="flex justify-center mb-4">
                    <AlertTriangle className="w-12 h-12 text-rollops-accent" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-[#EBFFEE]">Delete Account</CardTitle>
                    <p className="text-[#A9A3A3] mt-2">
                    This action cannot be undone. All your data will be permanently deleted.
                    </p>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">

                    {error && (
                        <div className="bg-red-900/20 border border-red-500 rounded-lg p-3 flex items-center space-x-2">
                          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                          <span className="text-red-200 text-sm">{error}</span>
                        </div>
                      )}
                    <div className="space-y-4">
                        <div>
                        <label htmlFor="email" className="block text-sm font-medium text-[#A9A3A3] mb-2">
                            Email Address *
                        </label>
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="Enter your email address"
                            maxLength={254}
                            className="bg-[#221F1F] border-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] focus:border-[#9B1421] focus:ring-[#9B1421]"
                            required
                          disabled={isLoading}/>
                        </div>

                        <div>
                        <label htmlFor="password" className="block text-sm font-medium text-[#A9A3A3] mb-2">
                            Password *
                        </label>
                        <Input
                            id="password"
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Enter your password"
                            maxLength={128}
                            className="bg-[#221F1F] border-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] focus:border-[#9B1421] focus:ring-[#9B1421]"
                            required
                            disabled={isLoading}
                        />
                        </div>
                    </div>

                    <div className="bg-[#221F1F] border border-[#504A4A] p-4 rounded-lg space-y-4">
                        <div>
                          <h3 className="font-semibold text-[#EBFFEE] mb-2">⚠️ What will be deleted:</h3>
                          <ul className="text-sm text-[#A9A3A3] space-y-1">
                            <li>• All your posts, comments, and personal content</li>
                            <li>• Your journal entries and notes</li>
                            <li>• Your personal profile data (avatar, date of birth, etc.)</li>
                            <li>• Your message content (conversations will show &quot;Message from deleted user&quot;)</li>
                          </ul>
                        </div>
                        
                        <div className="border-t border-[#504A4A] pt-3">
                          <p className="text-sm text-[#A9A3A3]">
                            <span className="font-semibold text-[#EBFFEE]">Important:</span> Your name, email, phone number, attendance records, disciplinary history, and business relationships will be retained for legal compliance, liability protection, and billing purposes. However, your name will not be displayed to other users in the application.{' '}
                            <a 
                              href="https://rollops.pro/privacy" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-rollops-accent hover:underline"
                            >
                              Learn more in our Privacy Policy
                            </a>.
                          </p>
                          <p className="text-sm text-[#A9A3A3] mt-2">
                            <span className="font-semibold text-[#EBFFEE]">This action cannot be undone.</span> You will need to create a new account if you want to use RollOps again in the future.
                          </p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <Link to="/" className="flex-1">
                        <Button
                            type="button"
                            variant="outline"
                            className="w-full border-[#504A4A] text-[#A9A3A3] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent"

                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        </Link>
                        <Button
                        type="submit"
                        className="flex-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] font-semibold"
                        disabled={isLoading}
                        >
                        {isLoading ? (
                            <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                            </>
                        ) : (
                            "Delete Account"
                        )}
                        </Button>
                    </div>
                    </form>
                </CardContent>
                </Card>
            </div>
        </main>
    </div>
    )
}
